import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { forkJoin, merge, Observable, of, throwError, timer } from 'rxjs';
import { environment } from '../../environments/environment';
import { CreateContentService } from './create-content.service';
import { SportType } from '../types/sport.type';
import { ContentType } from '../pages/content-creation/content-creation';
import { convertCreatedWorkoutSegmentsToApiFormat } from '../utils/convert-created-workout-to-api-format.helpers';
import { SectionsEditorService } from './sections-editor.service';
import { SectionModelType } from '../types/section.type';
import { WorkoutMode } from '@kinomap-dev/ng-ui-lib/core';
import { WorkoutDifficultyLevel } from '../types/workout-difficulty.type';
import { DIFFICULTY_LEVELS } from '../configs/sections-editor/workout-mode-colors.config';
import { getAverageDifficultyLevel } from '../utils/section-difficulty.utils';
import { durationToSeconds } from '../utils/duration.utils';
import { WORKOUT_MODE_IDS } from '../configs/sections-editor/workout-mode-ids.config';
import { WORKOUT_SPORT_IDS } from '../configs/sections-editor/workout-sport-ids.config';
import { VisibilityType } from '../types/visibility.type';
import { AnalyticsService } from './analytics.service';
import { MediaBlockService } from './media-block-service';

type UploadContentResponse = {
  token: string;
  s3VideoUploadUrl: string;
  s3GpsUploadUrl: string;
  unlocked_content: any[];
};

type UploadScenicMessage = {
  video_title: string;
  video_original_name: string;
  video_description: string | undefined;
  video_muted: number;
  video_status: number;
  video_start_time: number;
  video_offset: number;
  video_rotate: number;
  video_mode: number;
  video_audio: number;
  video_lang: number | null;
  video_hardware: string | null;
  video_equipment_id: number | null;
  video_vehicle_id: number | null;
  gps_extension: string;
  video_extension: string;
  'hidden_series[]'?: unknown;
  released_at: number | null;
};

type UploadCoachingMessage = {
  video_title: string;
  video_original_name: string;
  video_description: string | undefined;
  video_status: number;
  video_start_time: number;
  video_offset: number;
  video_rotate: number;
  video_mode: number;
  video_audio: number;
  video_hardware: string | null;
  video_equipment_id: number | null;
  video_extension: string;
  'hidden_series[]'?: unknown;
  released_at: number | null;
};

type UploadGPSMessage = {
  title: string;
  description: string | undefined;
  sport: SportType;
  gps_extension: string;
};

export type UploadWorkoutMessage = {
  name: string;
  description: string | undefined;
  instructions: string[];
  difficulty: number;
  duration: number;
  mode: number;
  modeText: string;
  speedUnit: string;
  sport: number;
  status: VisibilityType;
  themes: string[];
  segments: {
    name: string | undefined;
    color: string;
    h: number;
    m: number;
    s: number;
    optimalCadence?: number;
    order: number;
    value: number;
    value_secondary?: number;
  }[];
};

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly createContentService: CreateContentService = inject(CreateContentService);
  private readonly sectionsEditorService: SectionsEditorService = inject(SectionsEditorService);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly mediaBlockService: MediaBlockService = inject(MediaBlockService);
  s3VideoUploadUrl: WritableSignal<string | undefined> = signal(undefined);
  s3GpsUploadUrl: WritableSignal<string | undefined> = signal(undefined);
  workoutReady: WritableSignal<boolean> = signal(false);

  private readonly contentType: Signal<ContentType | undefined> =
    this.createContentService.contentType;

  readonly uploadSessionReady: Signal<boolean> = computed(() => {
    switch (this.contentType()) {
      case 'gps-only':
        return !!this.s3GpsUploadUrl();
      case 'workout':
        return this.workoutReady();
      case 'coaching':
        return !!this.s3VideoUploadUrl();
      default:
        return !!this.s3GpsUploadUrl() && !!this.s3VideoUploadUrl();
    }
  });

  transferSpeed: WritableSignal<number | undefined> = signal(undefined);
  transferTimeEstimation: WritableSignal<number | undefined> = signal(undefined);

  get audioMode(): number {
    switch (this.createContentService.audioMode()) {
      case 'recommended':
        return 1;
      case 'optionnal':
        return 2;
      default:
        return 0;
    }
  }

  get trainingMode(): number {
    switch (this.createContentService.speedMode()) {
      case 'variable_speed':
        return 2;
      default:
        return 1;
    }
  }

  get workoutDifficulty(): number {
    const mode: WorkoutMode | undefined = this.createContentService.workoutMode();
    const level: WorkoutDifficultyLevel | undefined = getAverageDifficultyLevel(
      this.createContentService.workoutSections().map((section: SectionModelType) => ({
        datas: this.sectionsEditorService.sectionDatas(section, mode),
        duration: section.duration.minutes * 60 + section.duration.seconds,
      })),
    );
    return level ? DIFFICULTY_LEVELS.indexOf(level) + 1 : 0;
  }

  get workoutDuration(): number {
    return this.createContentService
      .workoutSections()
      .reduce(
        (total: number, section: SectionModelType) => total + durationToSeconds(section.duration),
        0,
      );
  }

  scenic(): Observable<boolean> {
    return this.sendScenicUploadDataToApi().pipe(
      tap((response: UploadContentResponse) => {
        this.s3VideoUploadUrl.set(response.s3VideoUploadUrl);
        this.s3GpsUploadUrl.set(response.s3GpsUploadUrl);
      }),
      map(() => true),
      catchError(() => of(false)),
    );
  }

  gpsOnly(): Observable<boolean> {
    return this.sendGpsUploadDataToApi().pipe(
      tap((response: UploadContentResponse) => {
        this.s3GpsUploadUrl.set(response.s3GpsUploadUrl);
      }),
      map(() => true),
      catchError(() => of(false)),
    );
  }

  coaching(): Observable<boolean> {
    return this.sendCoachingUploadDataToApi().pipe(
      tap((response: UploadContentResponse) => {
        this.s3VideoUploadUrl.set(response.s3VideoUploadUrl);
        //TODO envoyer les intervals
      }),
      map(() => true),
      catchError(() => of(false)),
    );
  }

  workout(): Observable<boolean> {
    this.workoutReady.set(true);
    return of(true);
  }

  sendWorkout(minDurationMs: number = 4000): Observable<number> {
    const steps: number = 40;
    const progress$: Observable<number> = timer(0, minDurationMs / steps).pipe(
      take(steps),
      map((step: number) => Math.round((step / steps) * 100)),
      tap((progress: number) =>
        this.transferTimeEstimation.set((((100 - progress) / 100) * minDurationMs) / 1000),
      ),
    );
    const done$: Observable<number> = forkJoin([
      this.sendWorkoutUploadDataToApi(),
      timer(minDurationMs),
    ]).pipe(
      switchMap(([sent]: [boolean, number]) =>
        sent ? of(100) : throwError(() => new Error('Workout upload failed')),
      ),
      tap(() => this.transferTimeEstimation.set(undefined)),
    );
    return merge(progress$, done$);
  }

  private sendScenicUploadDataToApi(): Observable<UploadContentResponse> {
    const service: CreateContentService = this.createContentService;
    const url: string = `${environment.apiUrl}creator/uploads/scenic`;
    const videoStartTime: number = this.mediaBlockService.videoStartTime()!;
    const gpsStartTime: number = Math.floor(this.mediaBlockService.syncedCoordinates()[0].coo_time);
    const message: UploadScenicMessage = {
      video_title: service.title()!,
      video_original_name: service.videoOriginalName()!,
      video_description: service.description(),
      video_muted: this.audioMode === 0 ? 1 : 0,
      video_start_time: videoStartTime,
      video_offset: videoStartTime - gpsStartTime,
      video_rotate: 0,
      video_mode: this.trainingMode,
      video_audio: this.audioMode,
      video_lang: this.createContentService.languageId()!,
      video_hardware: service.recordedWith()!,
      video_equipment_id: null,
      video_vehicle_id: this.createContentService.vehiculeId()!,
      gps_extension: this.createContentService.gpsExtention()!,
      video_extension: service.videoExtention()!,
      video_status: 0,
      released_at:
        this.createContentService.planningDate() != null
          ? Math.floor(new Date(this.createContentService.planningDate()!).getTime() / 1000)
          : null,
    };
    return this.http.post<UploadContentResponse>(url, { ...message });
  }

  private sendCoachingUploadDataToApi(): Observable<UploadContentResponse> {
    const service: CreateContentService = this.createContentService;
    const url: string = `${environment.apiUrl}creator/uploads/coaching`;
    const videoFile: File | undefined = this.mediaBlockService.videoFile();
    const message: UploadCoachingMessage = {
      video_title: service.title()!,
      video_original_name: service.videoOriginalName()!,
      video_description: service.description(),
      video_status: 0,
      video_start_time: Math.floor((videoFile?.lastModified ?? Date.now()) / 1000),
      video_offset: 0,
      video_rotate: 0,
      video_mode: this.trainingMode,
      video_audio: this.audioMode,
      video_hardware: service.recordedWith() || null,
      video_equipment_id: service.vehiculeId() ?? null,
      video_extension: service.videoExtention()!,
      released_at:
        this.createContentService.planningDate() != null
          ? Math.floor(new Date(this.createContentService.planningDate()!).getTime() / 1000)
          : null,
    };
    return this.http.post<UploadContentResponse>(url, { ...message });
  }

  private sendGpsUploadDataToApi(): Observable<UploadContentResponse> {
    const service: CreateContentService = this.createContentService;
    const url: string = `${environment.apiUrl}creator/uploads/mapmode`;
    const message: UploadGPSMessage = {
      title: service.title()!,
      description: service.description(),
      sport: service.sport() as SportType,
      gps_extension: this.createContentService.gpsExtention()!,
    };
    return this.http.post<UploadContentResponse>(url, { ...message });
  }

  updateWorkout(): Observable<boolean> {
    const url: string = `${environment.apiUrl}workout/${this.createContentService.editedContentId()}`;
    return this.http.patch(url, { data: JSON.stringify(this.workoutMessage()) }).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  deleteWorkout(): Observable<boolean> {
    const url: string = `${environment.apiUrl}workout/${this.createContentService.editedContentId()}`;
    return this.http.delete(url).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  private sendWorkoutUploadDataToApi(): Observable<boolean> {
    const url: string = `${environment.apiUrl}workout`;
    return this.http.post<boolean>(url, { data: JSON.stringify(this.workoutMessage()) }).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  private workoutMessage(): UploadWorkoutMessage {
    const service: CreateContentService = this.createContentService;
    return {
      name: service.title()!,
      description: service.description(),
      instructions: [],
      mode: WORKOUT_MODE_IDS[service.workoutMode()!],
      modeText: service.workoutMode()!,
      difficulty: this.workoutDifficulty,
      duration: this.workoutDuration,
      speedUnit: 'kmh',
      sport: WORKOUT_SPORT_IDS[service.workoutSport()!],
      status: service.isDeleted() ? 'deleted' : service.visibility()!,
      themes: service.workoutThemes(),
      segments: convertCreatedWorkoutSegmentsToApiFormat(
        service.workoutSections(),
        (section: SectionModelType) =>
          this.sectionsEditorService.sectionDatas(section, service.workoutMode()),
      ),
    };
  }

  uploadToS3(url: string, file: File): Observable<number> {
    return new Observable<number>((observer) => {
      const xhr: XMLHttpRequest = new XMLHttpRequest();
      const startTime: number = Date.now();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          observer.next(Math.round((event.loaded / event.total) * 100));
          const elapsedSeconds: number = (Date.now() - startTime) / 1000;
          if (elapsedSeconds > 0) {
            const speed: number = event.loaded / elapsedSeconds;
            this.transferSpeed.set(speed);
            const remaining: number = event.total - event.loaded;
            this.transferTimeEstimation.set(remaining / speed);
          }
        }
      });

      xhr.addEventListener('load', () => {
        observer.next(100);
        observer.complete();
      });

      xhr.addEventListener('error', () => {
        this.analyticsService.track('upload_failed', {
          content_type: this.createContentService.contentType(),
          step: 's3',
        });
        observer.error(new Error('S3 upload failed'));
      });

      xhr.open('PUT', url);
      xhr.send(file);

      return () => xhr.abort();
    });
  }

  reset(): void {
    this.s3GpsUploadUrl.set(undefined);
    this.s3VideoUploadUrl.set(undefined);
    this.workoutReady.set(false);
  }
}
